import AddIcon from '@mui/icons-material/Add'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import ButtonMobileComponent from 'components/button-mobile'
import { DeleteIcon } from 'components/icons'
import InputMobileComponent from 'components/input-mobile'
import React, { useCallback, useEffect, useState } from 'react'
import { ScheduleGroupProps } from 'types/CommonTypes'
import { UpdateSchoolInputProps } from 'types/SchoolTypes'
import {
  getTimeNumberFromString,
  getTimeStringFromNumber,
  MAX_SCHEDULE_GROUPS,
  MIN_SCHEDULE_GROUPS,
} from 'utils/constants'
import SettingsBox from './SettingsBox'

interface ScheduleManagementSettingsProps {
  initialScheduleGroups: ScheduleGroupProps[]
  onSave: (body: UpdateSchoolInputProps, loadingMsg?: string, cb?: () => void) => Promise<void>
}

const ScheduleManagementSettings: React.FC<ScheduleManagementSettingsProps> = ({ initialScheduleGroups, onSave }) => {
  /* eslint @typescript-eslint/no-var-requires: "off" */
  const cloneDeep = require('lodash.clonedeep')
  const [schoolScheduleGroups, setSchoolScheduleGroups] = useState<ScheduleGroupProps[]>(
    cloneDeep(initialScheduleGroups),
  )
  // Sync local state with props when props change
  useEffect(() => {
    /* eslint @typescript-eslint/no-var-requires: "off" */
    const cloneDeep = require('lodash.clonedeep')
    setSchoolScheduleGroups(cloneDeep(initialScheduleGroups))
  }, [initialScheduleGroups])

  // hasChanges is always derived
  /* eslint @typescript-eslint/no-var-requires: "off" */
  const isEqual = require('lodash.isequal')
  const hasChanges = !isEqual(schoolScheduleGroups, initialScheduleGroups)

  // Handlers
  const handleAddScheduleGroup = () => {
    if (schoolScheduleGroups.length < MAX_SCHEDULE_GROUPS) {
      setSchoolScheduleGroups([
        ...schoolScheduleGroups,
        {
          schoolScheduleGroupStartTime: 0,
          schoolScheduleGroupEndTime: 0,
          schoolScheduleGroupName: '',
        },
      ])
    }
  }

  const handleDeleteScheduleGroup = (index: number) => {
    if (schoolScheduleGroups.length > MIN_SCHEDULE_GROUPS) {
      const updatedScheduleGroups = [...schoolScheduleGroups]
      updatedScheduleGroups.splice(index, 1)
      setSchoolScheduleGroups(updatedScheduleGroups)
    }
  }

  const handleScheduleGroupNameChange = (index: number, value: string) => {
    const updatedScheduleGroups = [...schoolScheduleGroups]
    updatedScheduleGroups[index].schoolScheduleGroupName = value
    setSchoolScheduleGroups(updatedScheduleGroups)
  }

  const handleScheduleGroupStartTimeChange = (index: number, value: string) => {
    const updatedScheduleGroups = [...schoolScheduleGroups]
    updatedScheduleGroups[index].schoolScheduleGroupStartTime = getTimeNumberFromString(value)
    setSchoolScheduleGroups(updatedScheduleGroups)
  }

  const handleScheduleGroupEndTimeChange = (index: number, value: string) => {
    const updatedScheduleGroups = [...schoolScheduleGroups]
    updatedScheduleGroups[index].schoolScheduleGroupEndTime = getTimeNumberFromString(value)
    setSchoolScheduleGroups(updatedScheduleGroups)
  }

  const isInvalidScheduleGroupTime = (index: number): boolean => {
    const group = schoolScheduleGroups[index]
    return !!group && group.schoolScheduleGroupStartTime >= group.schoolScheduleGroupEndTime
  }

  const isInvalidScheduleGroupName = (index: number): boolean => {
    const group = schoolScheduleGroups[index]
    return !group?.schoolScheduleGroupName
  }

  const isInvalid: boolean = schoolScheduleGroups.some(
    (g) =>
      isInvalidScheduleGroupTime(schoolScheduleGroups.indexOf(g)) ||
      isInvalidScheduleGroupName(schoolScheduleGroups.indexOf(g)),
  )

  const handleSave = useCallback(async () => {
    if (hasChanges && !isInvalid) {
      await onSave({ schoolScheduleGroups }, 'Updating school schedules...')
    }
  }, [hasChanges, schoolScheduleGroups, onSave, isInvalid])

  return (
    <SettingsBox title='School Start and Dismissal Times'>
      <Box sx={{ my: 1 }}>
        <Typography variant='body1'>
          {'Create groups with distinct names for different start and end times.'}
        </Typography>
      </Box>
      {schoolScheduleGroups.map((scheduleGroup, index) => (
        <Box key={index} sx={{ p: 2, width: '100%', mb: 2, backgroundColor: '#f9f9f9' }}>
          <Grid container alignItems='center' spacing={2} data-testid='schedule-group-row'>
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <InputMobileComponent
                labeltext='Group Name'
                value={scheduleGroup.schoolScheduleGroupName}
                onChange={(v: string) => handleScheduleGroupNameChange(index, v)}
                isError={isInvalidScheduleGroupName(index)}
                helperText={isInvalidScheduleGroupName(index) ? 'Name is required' : ''}
                data_testid='group-name-input-testid'
              />
            </Grid>
            <Grid item xs={5} sm={5.5} md={3} lg={3} xl={3}>
              <InputMobileComponent
                labeltext='Start Time'
                type='time'
                value={getTimeStringFromNumber(scheduleGroup.schoolScheduleGroupStartTime)}
                onChange={(v: string) => handleScheduleGroupStartTimeChange(index, v)}
                isError={isInvalidScheduleGroupTime(index)}
                helperText={isInvalidScheduleGroupTime(index) ? 'Invalid time' : ''}
                data_testid='start-time-input-testid'
              />
            </Grid>
            <Grid item xs={5} sm={5.5} md={3} lg={3} xl={3}>
              <InputMobileComponent
                labeltext='End Time'
                type='time'
                value={getTimeStringFromNumber(scheduleGroup.schoolScheduleGroupEndTime)}
                onChange={(v: string) => handleScheduleGroupEndTimeChange(index, v)}
                isError={isInvalidScheduleGroupTime(index)}
                helperText={isInvalidScheduleGroupTime(index) ? 'Invalid time' : ''}
                data_testid='end-time-input-testid'
              />
            </Grid>
            {schoolScheduleGroups.length > 1 && (
              <Grid item xs={1} sm={1} md={3} lg={3} xl={3}>
                <IconButton
                  data-testid={`delete-schedule-group-${index}`}
                  aria-label='delete'
                  color='error'
                  onClick={() => handleDeleteScheduleGroup(index)}
                  title='Delete Schedule'
                  sx={{
                    mt: 3,
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ButtonMobileComponent
          text='Add Group'
          icon={<AddIcon />}
          color='transparent'
          width='w-[188px]'
          onClick={handleAddScheduleGroup}
          disabled={schoolScheduleGroups.length >= MAX_SCHEDULE_GROUPS}
          data_testid='add-group-testid'
        />
      </Box>
      {hasChanges && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonMobileComponent
            text='Save'
            width='w-[100px]'
            disabled={isInvalid}
            onClick={handleSave}
            data_testid='group-add-save-testid'
          />
        </Box>
      )}
    </SettingsBox>
  )
}

export default ScheduleManagementSettings
